<template>
  <section :data-e2e="TaxCypressAttribute.probateCalculator.views.CALIFORNIA_BOND">
    <ProbateTaxDescriptionContent
      v-if="showDescription"
      :title="title"
      :subtitle="subtitle"
      :probate-title="probateTitle"
      :probate-description="probateDescription"
      :cost-description="costDescription"
      :cost-title="costTitle"
      @scroll-to-calculator="scrollToCalculator"
      :data-e2e="TaxCypressAttribute.probateCalculator.views.DESCRIPTION"
    />
    <section ref="calculator" class="is-justify-content-center columns">
      <div class="column is-justify-content-center is-5 px-6 py-5">
        <div class="has-border-light has-border-radius px-6 py-5">
          <div class="mb-6">
            <h2 class="is-size-2 has-text-centered has-text-weight-semibold m-b-10 is-capitalized">
              {{ $t('tax.probate-bond-calculator') }}
            </h2>
            <p class="m-b-20 m-l-2 has-text-weight-medium">
              {{ $t('tax.bond-calculator-california-calculator-subtitle') }}
            </p>
          </div>

          <ValidationObserver ref="validationObserver" v-slot="{ invalid }">
            <FormCurrency
              v-model.number="probateTaxData.inputs.estateGrossValue"
              :rules="{ required: true, min_value: 0 }"
              icon="currency-dollar"
              :placeholder="$t('tax.estate-gross-value')"
              :show-currency-select="false"
              :has-fixed-label="true"
              :currency="PredefinedCurrency.USD.id"
              :data-e2e="TaxCypressAttribute.probateCalculator.inputs.ESTATE_GROSS_VALUE"
            >
              <template #subLabel>
                <div class="is-flex is-align-content-center">
                  <i class="icon cls cls-alert-circle mr-1"></i>
                  <small>{{ $t('tax.probate-calculator-dont-remove-debts') }}</small>
                </div>
              </template>
            </FormCurrency>

            <div class="is-flex is-justify-content-center">
              <ButtonView
                class="button m-b-20"
                :label="$t('tax.calculate')"
                type="is-primary"
                :expanded="true"
                :disabled="invalid"
                @click="calculateProbateTax"
                :data-e2e="TaxCypressAttribute.probateCalculator.buttons.ESTIMATE"
              />
            </div>
          </ValidationObserver>

          <section class="mt-5">
            <div class="columns">
              <div class="column is-size-2 has-text-centered has-text-weight-semibold m-b-10">
                {{ $t('tax.estimated-bond-premium') }}
              </div>
            </div>
            <div class="columns is-justify-content-center">
              <div
                class="column is-11 has-background-secondary has-text-centered has-border-radius py-4 is-size-2 has-text-weight-semibold"
              >
                {{ lowerAnimatedAmountObject.formattedAmount }}
                <span class="is-lowercase">
                  {{ $t('accounting.to') }}
                </span>
                {{ upperAnimatedAmountObject.formattedAmount }}
              </div>
            </div>
          </section>
          <section class="mt-5 pt-4">
            <ProbateCalculatorBreakdown :line-items="probateTaxData.lineItems" />
          </section>
        </div>
      </div>
      <LoadingView :active="isLoading" />
    </section>
  </section>
</template>

<script>
import FormCurrency from '@/modules/core/form/components/FormCurrency.vue';
import ValidationObserver from '/@/modules/core/form/components/ValidationObserver.vue';
import { TaxCypressAttribute } from '@/modules/tax/constants/TaxCypressAttribute';
import { PredefinedJurisdiction } from '@/modules/predefined/enums/PredefinedJurisdiction';
import { HasCalculatorMixin } from '@/modules/tax/mixins/HasCalculatorMixin';
import ProbateTaxDescriptionContent from '@/modules/tax/components/calculator/ProbateTaxDescriptionContent.vue';
import Lang from '@/modules/core/app/services/Lang';
import ProbateCalculatorBreakdown from '@/modules/tax/components/calculator/ProbateCalculatorBreakDown.vue';
import { Calculator } from '@/modules/tax/models/calculators/Calculator';
import TaxCalculatorService from '@/modules/tax/services/CalculatorService';
import { CalculatorType } from '@/modules/tax/constants/CalculatorType';
import { PredefinedCurrency } from '@/modules/predefined/enums/PredefinedCurrency';
import { AnimatedTaxAmount } from '@/modules/tax/models/calculators/AnimatedTaxAmount';
import ButtonView from '@/modules/core/ui/components/wrappers/ButtonView.vue';
import LoadingView from '@/modules/core/ui/components/wrappers/LoadingView.vue';

export default {
  compatConfig: {
    COMPONENT_V_MODEL: true
  },
  name: 'CaliforniaBondCalculator',
  mixins: [HasCalculatorMixin],
  components: {
    ButtonView,
    ProbateCalculatorBreakdown,
    ProbateTaxDescriptionContent,
    ValidationObserver,
    FormCurrency,
    LoadingView
  },
  props: {
    showDescription: {
      type: Boolean,
      default: true
    }
  },
  static: {
    TaxCypressAttribute,
    PredefinedCurrency
  },
  local() {
    return {
      title: Lang.t('tax.california-bond-calculator'),
      subtitle: Lang.t('tax.california-bond-calculator-subtitle'),
      costDescription: Lang.t('tax.california-bond-calculator-costs'),
      costTitle: Lang.t('tax.california-bond-calculator-costs-title'),
      probateTitle: Lang.t('tax.what-probate-bond-is'),
      probateDescription: Lang.t('tax.what-probate-bond-is-description')
    };
  },
  data() {
    return {
      isLoading: false,
      probateTaxData: Calculator.mapModel({
        jurisdictionId: PredefinedJurisdiction.CALIFORNIA.id,
        type: CalculatorType.BOND,
        inputs: {
          estateGrossValue: 0
        }
      }),
      lowerAnimatedAmountObject: new AnimatedTaxAmount({}),
      upperAnimatedAmountObject: new AnimatedTaxAmount({})
    };
  },

  methods: {
    async calculateProbateTax() {
      this.isLoading = true;
      try {
        this.probateTaxData = await TaxCalculatorService.calculate(this.probateTaxData);
      } finally {
        this.isLoading = false;
      }

      this.lowerAnimatedAmountObject.target = Number(this.probateTaxData?.calculations?.lowerValue);
      this.upperAnimatedAmountObject.target = Number(this.probateTaxData?.calculations?.upperValue);

      this.animateAmount(this.lowerAnimatedAmountObject);
      this.animateAmount(this.upperAnimatedAmountObject);
    }
  }
};
</script>
